// Check if code is running in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

// Device detection
export const isMobile = {
  Android: function() {
    return isBrowser && /Android/i.test(navigator.userAgent);
  },
  BlackBerry: function() {
    return isBrowser && /BlackBerry/i.test(navigator.userAgent);
  },
  iOS: function() {
    return isBrowser && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  },
  Opera: function() {
    return isBrowser && /Opera Mini/i.test(navigator.userAgent);
  },
  Windows: function() {
    return isBrowser && (/IEMobile/i.test(navigator.userAgent) || /WPDesktop/i.test(navigator.userAgent));
  },
  any: function() {
    return (
      this.Android() || 
      this.BlackBerry() || 
      this.iOS() || 
      this.Opera() || 
      this.Windows()
    );
  }
};

// Browser detection
export const isBrowserType = {
  Chrome: function() {
    return isBrowser && /Chrome/.test(navigator.userAgent) && !/(FxiOS|OPR|Edg|Edge|EdgA|EdgiOS)/i.test(navigator.userAgent);
  },
  Firefox: function() {
    return isBrowser && /Firefox|FxiOS/.test(navigator.userAgent);
  },
  Safari: function() {
    return isBrowser && /^((?!Chrome|Android).)*Safari/i.test(navigator.userAgent);
  },
  Edge: function() {
    return isBrowser && /Edg|Edge|EdgA|EdgiOS/i.test(navigator.userAgent);
  },
  Opera: function() {
    return isBrowser && /OPR|Opera/.test(navigator.userAgent);
  },
  IE: function() {
    return isBrowser && /Trident\/|MSIE/.test(navigator.userAgent);
  }
};

// Individual browser detection functions for easier use
export const isChrome = () => isBrowserType.Chrome();
export const isFirefox = () => isBrowserType.Firefox();
export const isSafari = () => isBrowserType.Safari();
export const isEdge = () => isBrowserType.Edge();
export const isOpera = () => isBrowserType.Opera();
export const isIE = () => isBrowserType.IE();

// OS detection
export const isOS = {
  Windows: function() {
    return isBrowser && /Win/.test(navigator.platform);
  },
  Mac: function() {
    return isBrowser && /Mac/.test(navigator.platform);
  },
  Linux: function() {
    return isBrowser && /Linux/.test(navigator.platform);
  },
  Android: function() {
    return isMobile.Android();
  },
  iOS: function() {
    return isMobile.iOS();
  }
};

// Helper variables for common checks
export const isIOS = isMobile.iOS();
export const isAndroid = isMobile.Android();
export const isMobileDevice = isMobile.any();
export const isDesktop = !isMobile.any();

// Check if PWA is running in standalone mode
export const isStandalone = () => {
  if (!isBrowser) return false;
  
  // Check for display-mode: standalone (most browsers)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check for iOS Safari standalone mode
  if ((window.navigator as any).standalone === true) {
    return true;
  }
  
  // Check for Android Chrome standalone mode
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true;
  }
  
  // Additional check for PWA mode
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true;
  }
  
  return false;
};

// Check if PWA installation is supported
export const isPWAInstallSupported = () => {
  if (!isBrowser) return false;
  
  // Check for basic PWA support
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasManifest = document.querySelector('link[rel="manifest"]') !== null;
  const hasHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  const isSupportedBrowser = isChrome() || isFirefox() || isEdge() || isSafari();
  
  console.log('PWA Installation Support Check:', {
    hasServiceWorker,
    hasPushManager,
    hasManifest,
    hasHTTPS,
    isSupportedBrowser,
    userAgent: navigator.userAgent
  });
  
  const hasBasicSupport = hasServiceWorker && hasManifest && hasHTTPS && isSupportedBrowser;
  
  return hasBasicSupport;
};

// Async function to check if running in incognito/private mode
export const isIncognito = async (): Promise<boolean> => {
  if (!isBrowser) return false;
  
  try {
    // Only check for incognito mode on supported browsers
    if (isChrome() || isFirefox()) {
      // Use localStorage as primary detection method
      const testKey = 'pwa-incognito-test';
      const testValue = 'test-value';
      
      try {
        // Try to set and get a value from localStorage
        localStorage.setItem(testKey, testValue);
        const retrieved = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        
        // If we can't retrieve the value, we're likely in incognito mode
        if (retrieved !== testValue) {
          console.log('Incognito mode detected via localStorage test');
          return true;
        }
      } catch (storageError) {
        console.log('localStorage test failed, trying IndexedDB:', storageError);
        // If localStorage fails, try IndexedDB as fallback
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            console.log('IndexedDB test timed out, assuming normal mode');
            resolve(false); // Assume normal mode if timeout
          }, 2000); // Increased timeout for mobile devices
          
          const db = indexedDB.open('pwa-incognito-test', 1);
          db.onerror = () => {
            clearTimeout(timeout);
            console.log('IndexedDB test failed, assuming incognito mode');
            resolve(true); // Incognito mode
          };
          db.onsuccess = () => {
            clearTimeout(timeout);
            console.log('IndexedDB test succeeded, assuming normal mode');
            db.result.close();
            resolve(false); // Normal mode
          };
        });
      }
    }
    
    console.log('Incognito detection completed, assuming normal mode');
    return false; // Assume normal mode for other browsers
  } catch (error) {
    console.log('Incognito detection failed, assuming normal mode:', error);
    return false; // Assume normal mode if detection fails
  }
};

// Check if running in PWA mode (installed)
export const isPWAMode = () => {
  return isStandalone();
};

// Check if running in browser mode (not installed)
export const isBrowserMode = () => {
  return !isStandalone();
};

// Get current display mode
export const getDisplayMode = () => {
  if (!isBrowser) return 'browser';
  
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
  
  return 'browser';
};

// Check if device is touch enabled
export const isTouchDevice = () => {
  return isBrowser && ('ontouchstart' in window || 
    (navigator as any).maxTouchPoints > 0 || 
    (navigator as any).msMaxTouchPoints > 0);
};

// Get device orientation
export const getOrientation = () => {
  if (!isBrowser) return 'landscape';
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
};

// Check if device is in dark mode
export const isDarkMode = () => {
  if (!isBrowser) return false;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};
